/** @jsx jsx */
import { css, jsx, useTheme } from "@emotion/react";
import {
  Clickable,
  findTreeItemIndexPath,
  generateUniqueId,
  Icon,
  useForkRef,
  useThemeProps,
} from "@wsui/base";
import { omit } from "lodash/fp";
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const treeMenuContext = createContext();

const TreeMenuList = forwardRef(function TreeMenuList(
  { as: Component = "ul", ...props },
  ref,
) {
  // const theme = useTheme();
  return (
    <Component
      ref={ref}
      {...props}
      css={css`
        list-style: none;
        padding: 0;
        margin: 0;
      `}
    />
  );
});

function TreeMenuItem({ as: Component = "li", ...props }) {
  let {
    color = "gray",
    hoverColor = "transparent",
    fontWeight = 400,
    divider,
    ...restProps
  } = useThemeProps({
    props,
    name: "TreeMenuItem",
  });
  const theme = useTheme();

  const ownerState = useContext(treeMenuContext);

  const { isItemExpanded } = ownerState;

  const key = props["data-tree-menu-item"];
  const isExpanded = isItemExpanded(key);

  return (
    <Component
      {...props}
      css={css`
        position: relative;
        font-weight: ${fontWeight};
        color: ${theme.getColor(color)};
        border-top: ${divider
          ? `1px solid ${theme.getColor([color, 200])}`
          : null};

        &:hover,
        &:focus-visible {
          background: ${theme.getColor([hoverColor, 50])};
        }

        ${isExpanded && `background: ${theme.getColor([hoverColor, 50])};`}
      `}
      {...restProps}
    />
  );
}

function TreeMenuItemHeader({ as: Component = "div", level, ...props }) {
  let {
    // color = "gray",
    activeColor = "transparent",
    // hoverColor = "transparent",
    // borderOffset = 0,
    hasChildren,
    ...restProps
  } = useThemeProps({
    props,
    name: "TreeMenuItemHeader",
  });

  restProps = omit(
    ["databaseId", "featuredImage", "isFrontPage", "menuOrder", "parentId"],
    restProps,
  );
  const theme = useTheme();
  const ownerState = useContext(treeMenuContext);

  const { isItemExpanded } = ownerState;

  const key = props["data-tree-menu-item-header"];
  const isExpanded = isItemExpanded(key);

  const backgroundColor = theme.getColor(activeColor);
  const fontColor = theme.getColor([activeColor, "text"]);

  return (
    <Component
      {...restProps}
      css={css`
        position: relative;
        display: grid;
        grid-template-columns: auto max-content;
        justify-content: space-between;
        align-items: center;
        padding-left: ${`calc(${level} * ${theme.getLength(
          ownerState.indentation,
        )})`};
        @supports not selector(:has(:focus-visible)) {
          &:focus-within {
            outline: 2px solid accentColor;
          }
        }
        &:has(:focus-visible) {
          outline: 2px solid accentColor;
        }

        ${isExpanded &&
        `
          color: ${fontColor};
          background: ${backgroundColor};
        `}
      `}
    />
  );
}

function TreeMenuLink({ as: Component = Clickable, ...props }) {
  let {
    fontSize = "inherit",
    lineHeight = "inherit",
    fontWeight = 400,
    padding = "0.5rem",
    ...restProps
  } = useThemeProps({
    props,
    name: "TreeMenuLink",
  });
  restProps = omit(
    ["databaseId", "featuredImage", "isFrontPage", "menuOrder", "parentId"],
    restProps,
  );
  const theme = useTheme();
  return (
    <Component
      css={css`
        ${theme.styleUtils.buttonReset}
        &:focus-visible {
          outline: none;
        }
        cursor: default;
        &.wsui-is-interactive {
          cursor: pointer;
        }
        padding: ${padding};
        min-width: 0;
        text-overflow: ellipsis;
        overflow: hidden;
        font-size: ${fontSize};
        line-height: ${lineHeight};
        font-weight: ${fontWeight};
        &[aria-current="page"] {
          font-weight: 700;
        }
      `}
      {...restProps}
    />
  );
}

function TreeMenuToggleButton({ as: Component = "button", ...props }) {
  const theme = useTheme();
  let { offset = "0.5rem", ...restProps } = useThemeProps({
    props,
    name: "TreeMenuToggleButton",
  });

  return (
    <Component
      {...restProps}
      css={css`
        ${theme.styleUtils.buttonReset}
        cursor: pointer;
        padding: ${offset};
        margin-left: calc(${offset} * -1);
        margin-right: 0.5em;
      `}
    />
  );
}

function TreeMenuChildren({ items, parentKey = null, level, ...restProps }) {
  const ownerState = useContext(treeMenuContext);

  let { isItemExpanded, toggleItem, expandItem, collapseIcon, expandIcon } =
    ownerState;

  // This runs before navRef
  const ulRef = (element) => {
    if (!element) return;
    [...element.children].forEach((listItem) => {
      let link = listItem.querySelector("[data-tree-menu-link]");
      link.tabIndex = -1;
    });
  };

  return (
    <TreeMenuList
      as="ul"
      ref={ulRef}
      aria-orientation="vertical"
      divider
      spacing={0}
      alignItems="stretch"
      dividerStart={level > 0}
      {...restProps}
    >
      {items.map(({ label, items, current, ...itemProps }, index) => {
        void current;
        let key = parentKey ? `${parentKey}-${index}` : String(index);
        let isExpanded = !!items?.length && isItemExpanded(key);
        let hasChildren = !!items?.length;
        return (
          <TreeMenuItem
            key={index}
            data-tree-menu-item={key}
            divider={level > 0 || index > 0}
          >
            <TreeMenuItemHeader data-tree-menu-item-header={key} level={level}>
              <TreeMenuLink
                {...itemProps}
                data-tree-menu-link={key}
                aria-expanded={hasChildren ? String(isExpanded) : null}
                onClick={
                  hasChildren
                    ? () => {
                        if (
                          itemProps.href ||
                          itemProps.url ||
                          itemProps.to ||
                          itemProps.uri
                        ) {
                          expandItem(key);
                        } else {
                          toggleItem(key);
                        }
                      }
                    : null
                }
              >
                {label}
              </TreeMenuLink>
              {hasChildren && (
                <TreeMenuToggleButton
                  aria-hidden="true"
                  type="button"
                  tabIndex="-1"
                  onClick={(event) => {
                    toggleItem(key);
                    event.target
                      .closest("[data-tree-menu-item]")
                      .querySelector("[data-tree-menu-link]")
                      .focus();
                  }}
                >
                  <Icon
                    size="1.5em"
                    {...(isExpanded ? collapseIcon : expandIcon)}
                  />
                </TreeMenuToggleButton>
              )}
            </TreeMenuItemHeader>
            {isExpanded && (
              <TreeMenuChildren
                items={items}
                parentKey={key}
                level={level + 1}
              />
            )}
          </TreeMenuItem>
        );
      })}
    </TreeMenuList>
  );
}

export default forwardRef(function TreeMenu(props, inRef) {
  let {
    items,
    collapseIcon = "subtract-circle",
    expandIcon = "add-circle",
    indentation = 6,
    id,
    ...restProps
  } = useThemeProps({ props, name: "TreeMenu" });
  const navElementRef = useRef(null);
  const [defaultId] = useState(generateUniqueId());

  if (!items.length) return null;

  collapseIcon =
    typeof collapseIcon === "string" ? { name: collapseIcon } : collapseIcon;
  expandIcon =
    typeof expandIcon === "string" ? { name: expandIcon } : expandIcon;
  id = id || defaultId;

  // TODO: Expand current item (and its ancestors)
  let currentItemPath = findTreeItemIndexPath(items, (item) => item.current);
  let currentItemKey = currentItemPath?.join("-");
  useEffect(() => {
    currentItemPath?.forEach((index, length, path) => {
      expandItem(path.slice(0, length).join("-"));
    });
  }, [currentItemKey]);

  const focusFirst = (element) => {
    let firstElement = navElementRef.current.querySelector(
      "[data-tree-menu-link]",
    );
    if (firstElement) {
      return focusItem(firstElement, element);
    }
  };

  const focusNext = (element) => {
    let allElements = [
      ...navElementRef.current.querySelectorAll("[data-tree-menu-link]"),
    ];
    let currentIndex = allElements.indexOf(element);
    let nextIndex = currentIndex + 1;
    let nextElement = allElements[nextIndex];
    if (nextElement) {
      return focusItem(nextElement, element);
    }
  };

  const focusPrevious = (element) => {
    let allElements = [
      ...navElementRef.current.querySelectorAll("[data-tree-menu-link]"),
    ];
    let currentIndex = allElements.indexOf(element);
    let previousIndex = currentIndex - 1;
    let previousElement = allElements[previousIndex];
    if (previousElement) {
      return focusItem(previousElement, element);
    }
  };

  const focusLast = (element) => {
    let allElements = [
      ...navElementRef.current.querySelectorAll("[data-tree-menu-link]"),
    ];
    let lastElement = allElements.at(-1);
    if (lastElement) {
      return focusItem(lastElement, element);
    }
  };

  // const focusNextSibling = (element) => {
  //   let listItem = element.closest("[data-tree-menu-item]");
  //   let nextListItem = listItem.nextElementSibling;
  //   let nextSibling = nextListItem?.querySelector("[data-tree-menu-link]");
  //   if (nextSibling) {
  //     return focusItem(nextSibling, element);
  //   }
  // };

  // const focusPrevSibling = (element) => {
  //   let listItem = element.closest("[data-tree-menu-item]");
  //   let prevListItem = listItem.previousElementSibling;
  //   let prevSibling = prevListItem?.querySelector("[data-tree-menu-link]");
  //   if (prevSibling) {
  //     return focusItem(prevSibling, element);
  //   }
  // };

  // const focusFirstSibling = (element) => {
  //   let listItem = element.closest("[data-tree-menu-item]");
  //   let firstListItem = listItem.parentElement.querySelector(
  //     "[data-tree-menu-item]",
  //   );
  //   let firstSibling = firstListItem?.querySelector("[data-tree-menu-link]");
  //   if (firstSibling) {
  //     return focusItem(firstSibling, element);
  //   }
  // };

  // const focusLastSibling = (element) => {
  //   let listItem = element.closest("[data-tree-menu-item]");
  //   let siblingListItems = listItem.parentElement.querySelectorAll(
  //     "[data-tree-menu-item]",
  //   );
  //   let lastListItem = [...siblingListItems].at(-1);
  //   let lastSibling = lastListItem?.querySelector("[data-tree-menu-link]");
  //   if (lastSibling) {
  //     return focusItem(lastSibling, element);
  //   }
  // };

  const focusFirstChild = (element) => {
    let listItem = element.closest("[data-tree-menu-item]");
    let childListItem = listItem.querySelector("[data-tree-menu-item]");
    let firstChild = childListItem?.querySelector("[data-tree-menu-link]");
    if (firstChild) {
      return focusItem(firstChild, element);
    }
  };

  const focusParent = (element) => {
    let listItem = element.closest("[data-tree-menu-item]");
    let parentListItem = listItem.parentElement.closest(
      "[data-tree-menu-item]",
    );
    let parent = parentListItem?.querySelector("[data-tree-menu-link]");
    if (parent) {
      return focusItem(parent, element);
    }
  };

  // const focusNextParentSibling = (element) => {
  //   let listItem = element.closest("[data-tree-menu-item]");
  //   let parentListItem = listItem.parentElement.closest(
  //     "[data-tree-menu-item]",
  //   );
  //   let nextParentSiblingListItem = parentListItem.nextElementSibling;
  //   let nextParentSibling = nextParentSiblingListItem?.querySelector(
  //     "[data-tree-menu-link]",
  //   );
  //   if (nextParentSibling) {
  //     return focusItem(nextParentSibling, element);
  //   }
  // };

  const [expandedItems, setExpandedItems] = useState([]);
  const expandItem = (key) => {
    if (expandedItems.includes(key)) return;
    setExpandedItems((expandedItems) => [...expandedItems, key]);
  };
  const collapseItem = (key) => {
    if (!expandedItems.includes(key)) return;
    setExpandedItems((expandedItems) =>
      expandedItems.filter((item) => item !== key),
    );
  };
  const toggleItem = (key) => {
    if (expandedItems.includes(key)) {
      setExpandedItems((expandedItems) =>
        expandedItems.filter((item) => item !== key),
      );
    } else {
      setExpandedItems((expandedItems) => [...expandedItems, key]);
    }
  };
  const isItemExpanded = (key) => expandedItems.includes(key);
  const focusItem = (element, currentFocus) => {
    if (typeof element === "string") {
      element = navElementRef.querySelector(
        `[data-tree-menu-link="${element}"]`,
      );
    }
    if (element) {
      currentFocus =
        currentFocus ||
        navElementRef.querySelector("[data-tree-menu-link]:focus");
      element.tabIndex = 0;
      element.focus();
      currentFocus.tabIndex = -1;
      return element;
    }
  };

  const handleKeyDown = (event) => {
    // console.log(event.key);
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusNext(event.target) || focusFirst(event.target);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusPrevious(event.target) || focusLast(event.target);
        break;
      case "ArrowRight":
        {
          event.preventDefault();
          let isExpanded = event.target.getAttribute("aria-expanded");
          if (isExpanded === "false") {
            expandItem(event.target.dataset.treeMenuLink);
          } else if (isExpanded === "true") {
            focusFirstChild(event.target);
          }
        }
        break;
      case "ArrowLeft":
        {
          event.preventDefault();
          let isExpanded = event.target.getAttribute("aria-expanded");
          if (isExpanded === "true") {
            collapseItem(event.target.dataset.treeMenuLink);
          } else {
            focusParent(event.target);
          }
        }
        break;
      case "Home":
        {
          event.preventDefault();
          focusFirst(event.target);
        }
        break;
      case "End":
        {
          event.preventDefault();
          focusLast(event.target);
        }
        break;
      case "Space":
      case " ":
        {
          let isExpanded = event.target.getAttribute("aria-expanded");
          if (isExpanded) {
            event.preventDefault();
            toggleItem(event.target.dataset.treeMenuLink);
          }
        }
        break;
      // TODO: Handle PageUp, PageDown, Esc?
      // TODO: Handle type-ahead as described here: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/tree_role#keyboard_interactions
    }
  };

  // This runs after ulRef
  const navRef = useForkRef([
    inRef,
    (element) => {
      if (!element) return;
      navElementRef.current = element;
      let firstLink = element.querySelector("[data-tree-menu-link]");
      firstLink.tabIndex = 0;
    },
  ]);

  const ownerState = {
    collapseIcon,
    collapseItem,
    expandedItems,
    expandIcon,
    expandItem,
    id,
    indentation,
    isItemExpanded,
    items,
    toggleItem,
    ...restProps,
  };

  return (
    <nav {...restProps} onKeyDown={handleKeyDown} ref={navRef}>
      <treeMenuContext.Provider value={ownerState}>
        <TreeMenuChildren items={items} level={0} />
      </treeMenuContext.Provider>
    </nav>
  );
});
